import { z } from "zod";

/**
 * ツールの定義（docs/87 §5.2「1か所に置き、3か所で使う」、docs/90 §2.2・★2）。
 * 同じ定義から (a) MCP の tools 一覧（段2）(b) OpenAPI（段2）(c) 検索窓の社内 LLM（mai-11 の OpenAI 互換のツール呼び出し。段1）を作る。
 * 実装（handler）は apps/api/src/tools/ に置き、画面の API と同じ問い合わせを使う。
 *   - name は「対象.動作」（docs/87 の形）。OpenAI 互換のツール呼び出しは名前に「.」を使えない＝openAiToolName で「_」に置き換える（docs/90 §6.1）
 *   - title・description は日本語（AI が読むので「いつ使うか」を 1 文で）
 *   - scope は許可の範囲（docs/87 §5.1）。画面のセッションは全部のスコープ（docs/90 §3）
 *   - mutating は書き込むか。検索のツールはどれも false（docs/90 §2.2・決定 4）
 */

/** スコープ（docs/87 §5.1） */
export const TOOL_SCOPES = [
  "company:read",
  "partners:read",
  "partners:write",
  "projects:read",
  "projects:write_draft",
  "estimates:read",
  "estimates:write_draft",
  "vendor_quotes:read",
  "vendor_quotes:write",
  "invoices:read",
  "documents:read",
  "offline_access",
] as const;
export type ToolScope = (typeof TOOL_SCOPES)[number];
export const ToolScope = z.enum(TOOL_SCOPES);

export type ToolDef<I extends z.ZodType = z.ZodType, O extends z.ZodType = z.ZodType> = {
  name: string;
  title: string;
  description: string;
  input: I;
  output: O;
  /** 呼ぶのに要るスコープ（どれか1つ。結果の種類ごとの絞り込みは handler が scopes を見る） */
  scope: readonly ToolScope[];
  mutating: boolean;
};

const NAME_RE = /^[a-z][a-z_]*\.[a-z][a-z_]*$/;

export function defineTool<I extends z.ZodType, O extends z.ZodType>(d: ToolDef<I, O>): ToolDef<I, O> {
  if (!NAME_RE.test(d.name)) throw new Error(`ツールの名前は「対象.動作」の形で（${d.name}）`);
  if (d.scope.length === 0) throw new Error(`ツール ${d.name} のスコープが空です`);
  return d;
}

/** OpenAI 互換のツールの名前（「.」は使えない） */
export const openAiToolName = (name: string): string => name.replace(/\./g, "_");

/** OpenAI 互換のツール定義（社内 LLM。docs/87 §5.2 (c)）。parameters は入力の Zod から作る JSON Schema */
export function openAiTool(t: ToolDef): { type: "function"; function: { name: string; description: string; parameters: Record<string, unknown> } } {
  const schema = z.toJSONSchema(t.input, { io: "input", unrepresentable: "any" }) as Record<string, unknown>;
  delete schema.$schema;
  return { type: "function", function: { name: openAiToolName(t.name), description: `${t.title}：${t.description}`, parameters: schema } };
}

/** スコープを持つか（セッションは全部） */
export const hasScope = (scopes: readonly ToolScope[], need: readonly ToolScope[]): boolean => need.some((s) => scopes.includes(s));
